import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload,
  FileText,
  Check,
  X,
  AlertCircle,
  RefreshCw,
  Trash2,
  Edit2,
  Building2,
  ChevronDown,
  ChevronUp,
  CheckSquare,
  Square,
  Percent,
  Calendar,
  Send,
  Copy,
  AlertTriangle,
  UserPlus,
  Layers,
  Merge
} from 'lucide-react';
import api from '../../services/api';

// Signal type badge colors
const getCategoryColor = (category) => {
  switch (category) {
    case 'bullish':
      return 'bg-green-100 text-green-700';
    case 'bearish':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// Confidence indicator
const ConfidenceBadge = ({ confidence }) => {
  const pct = Math.round((confidence || 0) * 100);
  let colorClass = 'bg-red-100 text-red-700';
  if (pct >= 80) colorClass = 'bg-green-100 text-green-700';
  else if (pct >= 60) colorClass = 'bg-yellow-100 text-yellow-700';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      <Percent className="w-3 h-3" />
      {pct}%
    </span>
  );
};

// Edit Modal for draft signal
function DraftEditModal({ isOpen, onClose, draft, buyers, signalTypes, onSave }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (draft) {
      setFormData({
        buyer_id: draft.buyer_id || '',
        signal_type: draft.signal_type || '',
        signal_category: draft.signal_category || 'neutral',
        signal_title: draft.signal_title || '',
        signal_description: draft.signal_description || '',
        signal_strength: draft.signal_strength || 5,
        signal_date: draft.signal_date ? draft.signal_date.split('T')[0] : '',
        therapeutic_area: draft.therapeutic_area || '',
        modality: draft.modality || ''
      });
    }
  }, [draft]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(draft.id, formData);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !draft) return null;

  const therapeuticAreas = [
    'Oncology', 'Immunology', 'Neurology', 'Cardiovascular', 'Metabolic',
    'Rare Disease', 'Infectious Disease', 'Respiratory', 'Ophthalmology',
    'Dermatology', 'Hematology', 'GI/Hepatology', 'MASH/NASH', 'Nephrology'
  ];

  const modalities = [
    'Small Molecule', 'Antibody', 'ADC', 'Bispecific', 'CAR-T', 'Cell Therapy',
    'Gene Therapy', 'mRNA', 'siRNA', 'Peptide', 'Protein', 'Vaccine', 'Radiopharmaceutical'
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Edit Draft Signal</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Original buyer name */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">LLM Extracted Buyer Name:</p>
            <p className="font-medium text-gray-900">{draft.buyer_name_raw || 'N/A'}</p>
          </div>

          {/* Buyer Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign Buyer *</label>
            <select
              value={formData.buyer_id}
              onChange={(e) => setFormData(prev => ({ ...prev, buyer_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              required
            >
              <option value="">Select Buyer</option>
              {buyers.map(buyer => (
                <option key={buyer.id} value={buyer.id}>
                  {buyer.company_name}
                </option>
              ))}
            </select>
          </div>

          {/* Signal Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signal Type</label>
              <select
                value={formData.signal_type}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Type</option>
                {signalTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.signal_category}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_category: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="bullish">Bullish</option>
                <option value="bearish">Bearish</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.signal_title}
              onChange={(e) => setFormData(prev => ({ ...prev, signal_title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.signal_description}
              onChange={(e) => setFormData(prev => ({ ...prev, signal_description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
            />
          </div>

          {/* Date and Strength */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Signal Date</label>
              <input
                type="date"
                value={formData.signal_date}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Strength: {formData.signal_strength}/10
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.signal_strength}
                onChange={(e) => setFormData(prev => ({ ...prev, signal_strength: parseInt(e.target.value) }))}
                className="w-full"
              />
            </div>
          </div>

          {/* Therapeutic Area and Modality */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Area</label>
              <select
                value={formData.therapeutic_area}
                onChange={(e) => setFormData(prev => ({ ...prev, therapeutic_area: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Area</option>
                {therapeuticAreas.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
              <select
                value={formData.modality}
                onChange={(e) => setFormData(prev => ({ ...prev, modality: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">Select Modality</option>
                {modalities.map(mod => (
                  <option key={mod} value={mod}>{mod}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SignalParser() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending', 'approved', 'all'
  const [buyers, setBuyers] = useState([]);
  const [signalTypes, setSignalTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [editingDraft, setEditingDraft] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);

  // Load initial data
  useEffect(() => {
    loadBuyers();
    loadSignalTypes();
    loadDrafts();
  }, []);

  const loadBuyers = async () => {
    try {
      const response = await api.get('/admin/buyers?limit=500');
      setBuyers(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load buyers:', err);
    }
  };

  const loadSignalTypes = async () => {
    try {
      const response = await api.get('/admin/signals/types');
      setSignalTypes(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load signal types:', err);
    }
  };

  const loadDrafts = async (batchId = null, status = statusFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (batchId) params.set('batch_id', batchId);
      if (status && status !== 'all') params.set('status', status);
      params.set('limit', '200');

      const response = await api.get(`/admin/signals/drafts?${params.toString()}`);
      setDrafts(response.data?.data?.drafts || []);
      setBatches(response.data?.data?.batches || []);
    } catch (err) {
      console.error('Failed to load drafts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle file drag and drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    console.log('handleFileSelect called', e.target.files);
    if (e.target.files && e.target.files[0]) {
      console.log('File selected:', e.target.files[0].name);
      setFile(e.target.files[0]);
    }
  };

  // Upload and parse file
  const handleUpload = async () => {
    console.log('handleUpload called, file:', file);
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('Starting upload for file:', file.name, file.size);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log('Sending request to /admin/signals/parse-file');
      const response = await api.post('/admin/signals/parse-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000 // 2 minute timeout for LLM processing
      });
      console.log('Upload response:', response);

      if (response.data?.success) {
        setFile(null);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setSelectedBatch(response.data.data.batch_id);
        loadDrafts(response.data.data.batch_id);
        alert(`Successfully extracted ${response.data.data.total_signals} signals!`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to parse file: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      // Always reset file input on error too
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === drafts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(drafts.map(d => d.id)));
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Batch actions
  const handleBatchAction = async (action) => {
    if (selectedIds.size === 0) return;

    try {
      await api.post('/admin/signals/drafts/batch-update', {
        ids: Array.from(selectedIds),
        action
      });
      setSelectedIds(new Set());
      loadDrafts(selectedBatch);
    } catch (err) {
      console.error('Batch action error:', err);
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    }
  };

  // Update draft
  const handleUpdateDraft = async (id, data) => {
    try {
      await api.put(`/admin/signals/drafts/${id}`, data);
      loadDrafts(selectedBatch);
    } catch (err) {
      console.error('Update error:', err);
      throw err;
    }
  };

  // Delete draft
  const handleDeleteDraft = async (id) => {
    if (!confirm('Delete this draft signal?')) return;

    try {
      await api.delete(`/admin/signals/drafts/${id}`);
      loadDrafts(selectedBatch, statusFilter);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  // Quick create buyer and assign to draft
  const handleQuickCreateBuyer = async (draftId, buyerNameRaw) => {
    if (!buyerNameRaw) return;

    // Clean up name - take first company if multiple
    let buyerName = buyerNameRaw.trim();
    if (buyerName.includes('/') || buyerName.includes('+')) {
      buyerName = buyerName.split(/[\/\+]/)[0].trim();
    }

    const confirmed = confirm(`Create new buyer "${buyerName}" and assign to this signal?`);
    if (!confirmed) return;

    try {
      // Create buyer
      const createResponse = await api.post('/admin/buyers', {
        company_name: buyerName,
        is_active: true
      });

      const newBuyerId = createResponse.data?.data?.id;
      if (!newBuyerId) throw new Error('Failed to create buyer');

      // Update draft with new buyer_id
      await api.put(`/admin/signals/drafts/${draftId}`, {
        buyer_id: newBuyerId
      });

      // Refresh buyers list and drafts
      loadBuyers();
      loadDrafts(selectedBatch, statusFilter);

      alert(`Created buyer "${buyerName}" and assigned to signal!`);
    } catch (err) {
      console.error('Quick create buyer error:', err);
      alert('Failed to create buyer: ' + (err.response?.data?.error || err.message));
    }
  };

  // Publish approved signals
  const handlePublish = async () => {
    const approvedDrafts = drafts.filter(d => d.status === 'approved' || selectedIds.has(d.id));
    if (approvedDrafts.length === 0) {
      alert('No signals selected or approved to publish');
      return;
    }

    if (!confirm(`Publish ${selectedIds.size > 0 ? selectedIds.size : 'approved'} signals to buyer_signals?`)) {
      return;
    }

    setPublishing(true);
    try {
      // First approve selected if any
      if (selectedIds.size > 0) {
        await api.post('/admin/signals/drafts/batch-update', {
          ids: Array.from(selectedIds),
          action: 'approve'
        });
      }

      // Then publish
      const response = await api.post('/admin/signals/drafts/publish', {
        batch_id: selectedBatch
      });

      // Build result message
      let message = `Published ${response.data.published} signals!`;
      if (response.data.skippedDuplicates > 0) {
        message += `\n\nSkipped ${response.data.skippedDuplicates} duplicate(s):`;
        if (response.data.duplicates) {
          response.data.duplicates.forEach(dup => {
            message += `\n• ${dup.title}`;
          });
        }
      }
      if (response.data.errors && response.data.errors.length > 0) {
        message += `\n\nErrors (${response.data.errors.length}):`;
        response.data.errors.forEach(err => {
          message += `\n• ${err.title}: ${err.error}`;
        });
      }

      alert(message);
      setSelectedIds(new Set());
      loadDrafts(selectedBatch, statusFilter);
    } catch (err) {
      console.error('Publish error:', err);
      alert('Failed to publish: ' + (err.response?.data?.error || err.message));
    } finally {
      setPublishing(false);
    }
  };

  // Load existing duplicates in buyer_signals
  const loadDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await api.get('/admin/signals/duplicates');
      setDuplicates(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load duplicates:', err);
      alert('Failed to load duplicates: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingDuplicates(false);
    }
  };

  // Delete a single signal
  const handleDeleteSignal = async (signalId, title) => {
    if (!confirm(`Delete signal: "${title}"?`)) return;
    try {
      await api.delete(`/admin/signals/${signalId}`);
      loadDuplicates();
    } catch (err) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    }
  };

  // Merge duplicates (keep first, delete rest)
  const handleMergeDuplicates = async (group) => {
    const keepId = group.signals[0].id;
    const deleteIds = group.signals.slice(1).map(s => s.id);

    if (!confirm(`Keep "${group.signals[0].title}" and delete ${deleteIds.length} duplicate(s)?`)) return;

    try {
      await api.post('/admin/signals/merge-duplicates', {
        keep_id: keepId,
        delete_ids: deleteIds
      });
      loadDuplicates();
    } catch (err) {
      alert('Failed to merge: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Signal Parser</h1>
          <p className="text-gray-600">Upload documents and extract BD signals using AI</p>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Document</h2>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">Drag and drop your file here, or</p>
          <label className="inline-block">
            <span className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium cursor-pointer">
              Browse Files
            </span>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleFileSelect}
            />
          </label>
          <p className="text-sm text-gray-500 mt-2">Supports PDF, Word, Excel, TXT (max 50MB)</p>
        </div>

        {file && (
          <div className="mt-4 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-red-600" />
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFile(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Parse File
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        {/* Status Filter */}
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Status:</span>
          {['pending', 'approved', 'all'].map(status => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); loadDrafts(selectedBatch, status); }}
              className={`px-3 py-1 rounded-full text-sm capitalize ${
                statusFilter === status ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {status === 'all' ? 'All' : status}
            </button>
          ))}
        </div>

        {/* Batch Filter */}
        {batches.length > 0 && (
          <div className="flex items-center gap-4 flex-wrap border-t pt-3">
            <span className="text-sm font-medium text-gray-700">Batch:</span>
            <button
              onClick={() => { setSelectedBatch(null); loadDrafts(null, statusFilter); }}
              className={`px-3 py-1 rounded-full text-sm ${
                !selectedBatch ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {batches.map(batch => (
              <button
                key={batch.id}
                onClick={() => { setSelectedBatch(batch.id); loadDrafts(batch.id, statusFilter); }}
                className={`px-3 py-1 rounded-full text-sm flex items-center gap-1 ${
                  selectedBatch === batch.id ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FileText className="w-3 h-3" />
                {decodeURIComponent(escape(batch.file_name))}
                <span className="ml-1 opacity-75">({batch.total_signals})</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Batch Actions */}
      {drafts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSelectAll}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {selectedIds.size === drafts.length ? (
                  <CheckSquare className="w-5 h-5 text-red-600" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
                {selectedIds.size === drafts.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-sm text-gray-500">
                {selectedIds.size} selected of {drafts.length}
              </span>
              {drafts.filter(d => d.is_duplicate).length > 0 && (
                <span className="text-sm text-orange-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {drafts.filter(d => d.is_duplicate).length} duplicate(s)
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBatchAction('approve')}
                disabled={selectedIds.size === 0}
                className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <Check className="w-4 h-4" />
                Approve
              </button>
              <button
                onClick={() => handleBatchAction('reject')}
                disabled={selectedIds.size === 0}
                className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={handlePublish}
                disabled={publishing || (selectedIds.size === 0 && !drafts.some(d => d.status === 'approved'))}
                className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-1"
              >
                {publishing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Publish Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drafts List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
          </div>
        ) : drafts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No draft signals</p>
            <p className="text-sm">Upload a document to extract signals</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {drafts.map(draft => (
              <div
                key={draft.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  selectedIds.has(draft.id) ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <button
                    onClick={() => toggleSelect(draft.id)}
                    className="mt-1 flex-shrink-0"
                  >
                    {selectedIds.has(draft.id) ? (
                      <CheckSquare className="w-5 h-5 text-red-600" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {/* Buyer */}
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        {draft.buyer_name_matched ? (
                          <span className="font-medium text-gray-900">{draft.buyer_name_matched}</span>
                        ) : (
                          <span className="text-orange-600 font-medium flex items-center gap-1">
                            {draft.buyer_name_raw || 'Unassigned'}
                            {!draft.buyer_id && (
                              <>
                                <span className="text-xs">(needs assignment)</span>
                                {draft.buyer_name_raw && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickCreateBuyer(draft.id, draft.buyer_name_raw);
                                    }}
                                    className="ml-1 p-1 text-green-600 hover:bg-green-100 rounded"
                                    title={`Create buyer "${draft.buyer_name_raw}"`}
                                  >
                                    <UserPlus className="w-3 h-3" />
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        )}
                      </div>

                      {/* Signal Type Badge */}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getCategoryColor(draft.signal_category)}`}>
                        {draft.signal_type}
                      </span>

                      {/* Confidence */}
                      <ConfidenceBadge confidence={draft.confidence} />

                      {/* Duplicate Badge */}
                      {draft.is_duplicate && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"
                          title={draft.duplicate_of ? `Duplicate of: ${draft.duplicate_of.signal_title}` : 'Duplicate signal'}
                        >
                          <Copy className="w-3 h-3" />
                          Duplicate
                        </span>
                      )}

                      {/* Date */}
                      {draft.signal_date && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(draft.signal_date)}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <p className="text-gray-900 font-medium">{draft.signal_title}</p>

                    {/* Description */}
                    {draft.signal_description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{draft.signal_description}</p>
                    )}

                    {/* Tags */}
                    <div className="flex items-center gap-2 mt-2">
                      {draft.therapeutic_area && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                          {draft.therapeutic_area}
                        </span>
                      )}
                      {draft.modality && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                          {draft.modality}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        Strength: {draft.signal_strength}/10
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditingDraft(draft)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteDraft(draft.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Duplicate Management Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <button
          onClick={() => {
            setShowDuplicates(!showDuplicates);
            if (!showDuplicates && duplicates.length === 0) {
              loadDuplicates();
            }
          }}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-orange-600" />
            <div className="text-left">
              <h2 className="text-lg font-semibold text-gray-900">Duplicate Manager</h2>
              <p className="text-sm text-gray-600">Find and merge duplicate signals in published data</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {duplicates.length > 0 && (
              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                {duplicates.length} groups
              </span>
            )}
            {showDuplicates ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>

        {showDuplicates && (
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">
                Signals with the same buyer, type, and month are grouped as potential duplicates.
              </p>
              <button
                onClick={loadDuplicates}
                disabled={loadingDuplicates}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-1"
              >
                <RefreshCw className={`w-4 h-4 ${loadingDuplicates ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingDuplicates ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading duplicates...
              </div>
            ) : duplicates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Check className="w-8 h-8 mx-auto mb-2 text-green-500" />
                No duplicate signals found
              </div>
            ) : (
              <div className="space-y-4">
                {duplicates.map((group, idx) => (
                  <div key={idx} className="border border-orange-200 rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <div className="bg-orange-50 p-3 flex items-center justify-between">
                      <div>
                        <span className="font-medium text-gray-900">{group.buyer_name}</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-sm text-gray-600">{group.signal_type}</span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-sm text-gray-600">
                          {group.signal_month ? new Date(group.signal_month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'No date'}
                        </span>
                        <span className="ml-2 px-2 py-0.5 bg-orange-200 text-orange-800 rounded text-xs font-medium">
                          {group.count} signals
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setDuplicates(prev => prev.filter((_, i) => i !== idx))}
                          className="px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-sm flex items-center gap-1"
                          title="Not duplicates - keep all signals"
                        >
                          <X className="w-4 h-4" />
                          Not Duplicates
                        </button>
                        <button
                          onClick={() => handleMergeDuplicates(group)}
                          className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm flex items-center gap-1"
                          title="Keep first, delete rest"
                        >
                          <Merge className="w-4 h-4" />
                          Merge All
                        </button>
                      </div>
                    </div>

                    {/* Individual Signals */}
                    <div className="divide-y divide-gray-100">
                      {group.signals.map((signal, sIdx) => (
                        <div
                          key={signal.id}
                          className={`p-3 flex items-center justify-between ${sIdx === 0 ? 'bg-green-50' : 'bg-white'}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {sIdx === 0 && (
                                <span className="px-1.5 py-0.5 bg-green-200 text-green-800 rounded text-xs font-medium">
                                  KEEP
                                </span>
                              )}
                              <span className="font-medium text-gray-900 text-sm">{signal.title}</span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span>Date: {formatDate(signal.date)}</span>
                              <span>Source: {signal.source || 'Unknown'}</span>
                              <span>Created: {formatDate(signal.created_at)}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteSignal(signal.id, signal.title)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete this signal"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <DraftEditModal
        isOpen={!!editingDraft}
        onClose={() => setEditingDraft(null)}
        draft={editingDraft}
        buyers={buyers}
        signalTypes={signalTypes}
        onSave={handleUpdateDraft}
      />
    </div>
  );
}
